import { yupResolver } from '@hookform/resolvers/yup'
import * as Sentry from '@sentry/nextjs'
import useTranslation from 'next-translate/useTranslation'
import { FormEvent, useCallback, useState } from 'react'
import { DefaultValues, useForm } from 'react-hook-form'
import * as yup from 'yup'
import { isValidPhoneNumber } from 'react-phone-number-input'
import { useUI } from '@components/ui'
import {
  isErrorResponseError,
  useTransitionOrderToState,
} from '@lib/api/cart/transitionOrderToState'
import { useSetOrderShippingAddress } from '@lib/api/cart/setOrderShippingAddress'
import { useSetOrderBillingAddress } from '@lib/api/cart/setOrderBillingAddress'
import { areAddressesEqual, getOrderAddress } from '../helpers'
import {
  OrderTransitionError,
  OrderValidationError,
  VertexTaxError,
} from '../constants'

export interface AddressFormData {
  email: string
  zone: string
  firstName: string
  lastName: string
  address: string
  addressDetails: string
  province: string
  zipcode: string
  city: string
  phone: string
}

export interface AddressesFormData {
  shippingData: AddressFormData
  billingData: AddressFormData
}

export interface AddressesFormError<T = AddressFormData> {
  form: keyof AddressesFormData
  field: keyof T
  messageKey?: string
}

export interface UseAddressFormArgs {
  currentShippingAddress?: DefaultValues<AddressFormData>
  currentBillingAddress?: DefaultValues<AddressFormData>
}

export const useAddressForm = ({
  currentShippingAddress,
  currentBillingAddress,
}: UseAddressFormArgs) => {
  const { t } = useTranslation()

  const { setSidebarView } = useUI()
  const { transitionOrderToState } = useTransitionOrderToState()
  const { setOrderShippingAddress } = useSetOrderShippingAddress()
  const { setOrderBillingAddress } = useSetOrderBillingAddress()
  const [loading, setLoading] = useState(false)

  const onExit = useCallback(async () => {
    await transitionOrderToState({
      state: 'AddingItems',
    })
  }, [transitionOrderToState])

  const [isBillingChecked, setIsBillingChecked] = useState(
    areAddressesEqual(currentBillingAddress, currentShippingAddress)
  )

  const schema = yup
    .object({
      email: yup
        .string()
        .required(t('checkout:email_field') + ' ' + t('checkout:is_required'))
        .email(t('checkout:email_field_invalid')),
      zone: yup
        .string()
        .required(t('checkout:zone_field') + ' ' + t('checkout:is_required')),
      firstName: yup
        .string()
        .required(
          t('checkout:first_name_field') + ' ' + t('checkout:is_required')
        ),
      lastName: yup
        .string()
        .required(
          t('checkout:last_name_field') + ' ' + t('checkout:is_required')
        ),
      address: yup
        .string()
        .required(
          t('checkout:address_field') + ' ' + t('checkout:is_required')
        ),
      addressDetails: yup.string(),
      province: yup
        .string()
        .required(
          t('checkout:province_field') + ' ' + t('checkout:is_required')
        ),
      zipcode: yup
        .string()
        .required(t('checkout:zipcode_field') + ' ' + t('checkout:is_required'))
        .matches(/^[\d\s]{5}/, t('checkout:zipcode_field_invalid')),
      city: yup
        .string()
        .required(t('checkout:city_field') + ' ' + t('checkout:is_required')),
      phone: yup
        .string()
        .required(t('checkout:phone_field') + ' ' + t('checkout:is_required'))
        .test(
          'validate phone number',
          t('checkout:phone_field_invalid'),
          (shippingPhoneValue) => isValidPhoneNumber(shippingPhoneValue ?? '')
        ),
    })
    .required(t('checkout:missing_fields'))

  const {
    handleSubmit: handleShippingSubmit,
    control: shippingControl,
    clearErrors: clearShippingErrors,
    formState: { errors: shippingErrors },
    setError: setShippingError,
  } = useForm<AddressFormData>({
    resolver: yupResolver(schema),
    mode: 'onSubmit',
    defaultValues: currentShippingAddress,
  })

  const billingSchema = yup
    .object({
      zone: yup
        .string()
        .required(t('checkout:zone_field') + ' ' + t('checkout:is_required')),
      firstName: yup
        .string()
        .required(
          t('checkout:first_name_field') + ' ' + t('checkout:is_required')
        ),
      lastName: yup
        .string()
        .required(
          t('checkout:last_name_field') + ' ' + t('checkout:is_required')
        ),
      address: yup
        .string()
        .required(
          t('checkout:address_field') + ' ' + t('checkout:is_required')
        ),
      addressDetails: yup.string(),
      province: yup
        .string()
        .required(
          t('checkout:province_field') + ' ' + t('checkout:is_required')
        ),
      zipcode: yup
        .string()
        .required(t('checkout:zipcode_field') + ' ' + t('checkout:is_required'))
        .matches(/^[\d\s]{5}/, t('checkout:zipcode_field_invalid')),
      city: yup
        .string()
        .required(t('checkout:city_field') + ' ' + t('checkout:is_required')),
      phone: yup
        .string()
        .required(t('checkout:phone_field') + ' ' + t('checkout:is_required'))
        .test(
          'validate phone number',
          t('checkout:phone_field_invalid'),
          (billingPhoneValue) => isValidPhoneNumber(billingPhoneValue ?? '')
        ),
    })
    .required(t('checkout:missing_fields'))

  const {
    handleSubmit: handleBillingSubmit,
    control: billingControl,
    clearErrors: clearBillingErrors,
    formState: { errors: billingErrors },
    setError: setBillingError,
  } = useForm<Omit<AddressFormData, 'email'>>({
    resolver: yupResolver(billingSchema),
    mode: 'onSubmit',
    defaultValues: currentBillingAddress,
  })

  const setFormErrors = useCallback(
    (formErrors: AddressesFormError[]) => {
      formErrors.forEach(({ field, messageKey }) => {
        setShippingError(`${field}`, {
          type: 'custom',
          message: t(messageKey ?? 'checkout:invalid_form_field', { field }),
          types: { value: true, validate: true },
        })
      })
    },
    [setShippingError, t]
  )

  const setBillingFormErrors = useCallback(
    (formErrors: AddressesFormError<Omit<AddressFormData, 'email'>>[]) => {
      formErrors.forEach(({ field, messageKey }) => {
        setBillingError(`${field}`, {
          type: 'custom',
          message: t(messageKey ?? 'checkout:invalid_form_field', { field }),
          types: { value: true, validate: true },
        })
      })
    },
    [setBillingError, t]
  )

  const submitAddresses = useCallback(
    async ({ shippingData, billingData }: AddressesFormData) => {
      setLoading(true)
      clearShippingErrors()
      clearBillingErrors()

      const orderShippingAddress = getOrderAddress(shippingData)
      const orderBillingAddress = getOrderAddress(billingData)
      try {
        await setOrderShippingAddress({
          createAddressInput: orderShippingAddress,
        })
        await setOrderBillingAddress({
          createAddressInput: orderBillingAddress,
        })
      } catch (error) {
        if (
          isErrorResponseError(error) &&
          error.response.errors[0]?.message ===
            OrderValidationError.INVALID_EMAIL_IN_ADDRESS
        ) {
          setFormErrors([{ form: 'shippingData', field: 'email' }])
          setLoading(false)
        } else {
          Sentry.captureException(error)
          console.error(error)
        }

        return
      }

      const transition = await transitionOrderToState({
        state: 'EnteringShippingMethod',
      })

      if (
        transition.transitionOrderToState?.__typename ===
        'OrderStateTransitionError'
      ) {
        switch (transition.transitionOrderToState.transitionError) {
          case OrderTransitionError.USER_NOT_CONNECTED:
            setSidebarView('WALLET_VIEW', () =>
              submitAddresses({ shippingData, billingData })
            )

            break
          case OrderTransitionError.SHIPPING_FORM_INVALID_FIELD_EMAIL:
          case OrderTransitionError.SHIPPING_FORM_MISSING_FIELD_EMAIL:
            setFormErrors([{ form: 'shippingData', field: 'email' }])
            break
          case OrderTransitionError.SHIPPING_FORM_INVALID_FIELD_ZIP_CODE:
          case OrderTransitionError.SHIPPING_FORM_MISSING_FIELD_ZIP_CODE:
            setFormErrors([{ form: 'shippingData', field: 'zipcode' }])
            break
          case OrderTransitionError.SHIPPING_FORM_INVALID_FIELD_PROVINCE:
          case OrderTransitionError.SHIPPING_FORM_MISSING_FIELD_PROVINCE:
            setFormErrors([{ form: 'shippingData', field: 'province' }])
            break
          case OrderTransitionError.SHIPPING_FORM_MISSING_FIELD_ADDRESS:
            setFormErrors([{ form: 'shippingData', field: 'address' }])
            break
          case OrderTransitionError.SHIPPING_FORM_MISSING_FIELD_FIRST_NAME:
            setFormErrors([{ form: 'shippingData', field: 'firstName' }])
            break
          case OrderTransitionError.SHIPPING_FORM_MISSING_FIELD_LAST_NAME:
            setFormErrors([{ form: 'shippingData', field: 'lastName' }])
            break
          case OrderTransitionError.SHIPPING_FORM_MISSING_FIELD_COUNTRY_CODE:
            setFormErrors([{ form: 'shippingData', field: 'zone' }])
            break
          case OrderTransitionError.SHIPPING_FORM_MISSING_FIELD_CITY:
            setFormErrors([{ form: 'shippingData', field: 'city' }])
            break
          case VertexTaxError.VERTEX_TAX_ERROR:
            setFormErrors([
              {
                form: 'shippingData',
                field: 'zipcode',
                messageKey: 'checkout:vertex_tax_error',
              },
              { form: 'shippingData', field: 'province' },
            ])
            break
          case OrderTransitionError.BILLING_FORM_INVALID_FIELD_EMAIL:
          case OrderTransitionError.BILLING_FORM_MISSING_FIELD_EMAIL:
            setFormErrors([{ form: 'shippingData', field: 'email' }])
            break
          case OrderTransitionError.BILLING_FORM_INVALID_FIELD_ZIP_CODE:
          case OrderTransitionError.BILLING_FORM_MISSING_FIELD_ZIP_CODE:
            setBillingFormErrors([{ form: 'billingData', field: 'zipcode' }])
            break
          case OrderTransitionError.BILLING_FORM_INVALID_FIELD_PROVINCE:
          case OrderTransitionError.BILLING_FORM_MISSING_FIELD_PROVINCE:
            setBillingFormErrors([{ form: 'billingData', field: 'province' }])
            break
          case OrderTransitionError.BILLING_FORM_MISSING_FIELD_ADDRESS:
            setBillingFormErrors([{ form: 'billingData', field: 'address' }])
            break
          case OrderTransitionError.BILLING_FORM_MISSING_FIELD_FIRST_NAME:
            setBillingFormErrors([{ form: 'billingData', field: 'firstName' }])
            break
          case OrderTransitionError.BILLING_FORM_MISSING_FIELD_LAST_NAME:
            setBillingFormErrors([{ form: 'billingData', field: 'lastName' }])
            break
          case OrderTransitionError.BILLING_FORM_MISSING_FIELD_COUNTRY_CODE:
            setBillingFormErrors([{ form: 'billingData', field: 'zone' }])
            break
          case OrderTransitionError.BILLING_FORM_MISSING_FIELD_CITY:
            setBillingFormErrors([{ form: 'billingData', field: 'city' }])
            break
          default:
            break
        }
        setLoading(false)
      }
    },
    [
      clearShippingErrors,
      clearBillingErrors,
      transitionOrderToState,
      setOrderShippingAddress,
      setOrderBillingAddress,
      setFormErrors,
      setSidebarView,
      setBillingFormErrors,
    ]
  )

  const onSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      handleShippingSubmit((shippingData) => {
        if (isBillingChecked) {
          return submitAddresses({
            shippingData,
            billingData: shippingData,
          })
        }

        handleBillingSubmit((billingData) => {
          return submitAddresses({
            shippingData,
            billingData: { ...billingData, email: shippingData.email },
          })
        })(event).catch(() => {})
      })(event).catch(() => {})
    },
    [
      handleBillingSubmit,
      handleShippingSubmit,
      isBillingChecked,
      submitAddresses,
    ]
  )

  return {
    handleShippingSubmit,
    shippingControl,
    shippingErrors,
    handleBillingSubmit,
    billingControl,
    billingErrors,
    isBillingChecked,
    setIsBillingChecked,
    loading,
    onExit,
    onSubmit,
  }
}
